// ===================================================
// MobileNav.js — 모바일 전용 하단 고정 네비게이션 바 (FNB)
//
// 데스크톱에서는 CSS로 숨겨집니다.
// iOS 네이티브 탭 바 스타일: SVG 아이콘, 이모지 없음
//
// 구조: 2개 탭 + 가운데 원형 FAB 버튼
//   📅 기록       → 캘린더/즐겨찾기/리포트 화면 (헤더 내 검색 아이콘 포함)
//   [+ FAB]      → 이야기하기 (새 기록 시작)
//   💬 나와의 대화 → 채팅 화면 열기 (헤더 내 설정 아이콘 포함)
// ===================================================

import './MobileNav.css';
import { UI_TEXT } from './content/uiText';
import { triggerTapHaptic } from './native/haptics';

// ── SVG 아이콘 컴포넌트 (Heroicons v2 Outline 기반) ──────────
// iOS SF Symbols 스타일: 선 굵기 1.5, 둥근 끝

function IconCreate() {
  return (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
      <path d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" />
    </svg>
  );
}

// 캘린더 아이콘 — '기록' 탭
function IconCalendar() {
  return (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
      <path d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z" />
    </svg>
  );
}

// 말풍선 아이콘 — '나와의 대화' 탭
function IconChat() {
  return (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
    </svg>
  );
}

// ── 메인 컴포넌트 ────────────────────────────────────────────
// onSearch, onOpenSettings는 각각 RecordsPage/ChatView 헤더 아이콘이 담당
function MobileNav({ mode, screen, onCreateRecord, onOpenCalendar, onOpenChat, hasUnreadChat }) {
  const T = UI_TEXT.mobileNav;

  // 각 탭의 활성 여부
  const createActive   = screen === 'landing' || screen === 'qa';
  const calendarActive = screen === 'calendar';
  const chatActive     = screen === 'chat';

  return (
    <nav className={`mobile-fnb ${mode}`}>

      {/* 기록 (캘린더 + 즐겨찾기 + 리포트) */}
      <button className={`fnb-btn ${calendarActive ? 'active' : ''}`} onClick={() => { triggerTapHaptic(); onOpenCalendar(); }}>
        <span className="fnb-icon-wrap"><IconCalendar /></span>
        <span className="fnb-label">{T.calendar}</span>
      </button>

      {/* 이야기하기 — 가운데 원형 FAB */}
      <div className="fnb-fab-slot">
        <button
          className={`fnb-fab ${createActive ? 'active' : ''}`}
          onClick={() => { triggerTapHaptic(); onCreateRecord(); }}
          aria-label={T.create}
        >
          <IconCreate />
        </button>
        <span className="fnb-fab-label">{T.create}</span>
      </div>

      {/* 나와의 대화 */}
      <button className={`fnb-btn ${chatActive ? 'active' : ''}`} onClick={() => { triggerTapHaptic(); onOpenChat(); }}>
        {/* 읽지 않은 AI 메시지가 있을 때 아이콘 위에 빨간 점 표시 */}
        <span className="fnb-icon-wrap fnb-icon-badge-wrap">
          <IconChat />
          {hasUnreadChat && <span className="fnb-unread-dot" />}
        </span>
        <span className="fnb-label">{T.chat}</span>
      </button>

    </nav>
  );
}

export default MobileNav;
